import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { UIIncident } from '../../../../../../../api/teamMetricsApi';
import { IncidentFilter, FilterableColumn, FILTERABLE_COLUMNS } from './types';

interface UseIncidentFiltersProps {
  incidents: UIIncident[];
  initialTeamFilter?: string;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, options?: { replace?: boolean }) => void;
}

function parseFiltersFromUrl(searchParams: URLSearchParams): IncidentFilter[] {
  const filters: IncidentFilter[] = [];
  const filterParam = searchParams.get('filters');
  if (filterParam) {
    try {
      const parsed = JSON.parse(decodeURIComponent(filterParam));
      if (Array.isArray(parsed)) {
        parsed.forEach((f: { column: string; values: string[]; isNot?: boolean }) => {
          if (FILTERABLE_COLUMNS.includes(f.column as FilterableColumn)) {
            filters.push({
              column: f.column as FilterableColumn,
              values: f.values,
              isNot: f.isNot,
            });
          }
        });
      }
    } catch {
      // Invalid JSON, ignore
    }
  }
  return filters;
}

function serializeFiltersToUrl(filters: IncidentFilter[]): string {
  if (filters.length === 0) return '';
  return encodeURIComponent(JSON.stringify(filters.map(f => ({
    column: f.column,
    values: f.values,
    ...(f.isNot ? { isNot: true } : {}),
  }))));
}

interface UseIncidentFiltersReturn {
  filters: IncidentFilter[];
  filteredIncidents: UIIncident[];
  availableValues: Record<FilterableColumn, string[]>;
  getAvailableValuesForColumn: (column: FilterableColumn) => string[];
  addFilter: (column: FilterableColumn, values: string[], isNot?: boolean) => void;
  removeFilter: (column: FilterableColumn) => void;
  updateFilter: (column: FilterableColumn, values: string[], isNot?: boolean) => void;
  clearAllFilters: () => void;
}

function getUniqueValues(incidents: UIIncident[], column: FilterableColumn): string[] {
  const seen = new Map<string, string>();
  incidents.forEach((incident) => {
    const value = incident.fields[column];
    if (value && value.trim()) {
      const lower = value.trim().toLowerCase();
      if (!seen.has(lower)) {
        seen.set(lower, value.trim());
      }
    }
  });
  return Array.from(seen.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

function matchesFilter(incident: UIIncident, filter: IncidentFilter): boolean {
  const fieldValue = incident.fields[filter.column];
  if (!fieldValue) return filter.isNot ? true : false;
  const normalizedValue = fieldValue.toLowerCase();
  const matches = filter.values.some((v) => normalizedValue === v.toLowerCase());
  return filter.isNot ? !matches : matches;
}

function filterIncidentsExcludingColumn(
  incidents: UIIncident[],
  filters: IncidentFilter[],
  excludeColumn: FilterableColumn
): UIIncident[] {
  const otherFilters = filters.filter((f) => f.column !== excludeColumn);
  if (otherFilters.length === 0) return incidents;

  return incidents.filter((incident) => otherFilters.every((filter) => matchesFilter(incident, filter)));
}

export function useIncidentFilters({
  incidents,
  initialTeamFilter,
  searchParams,
  setSearchParams,
}: UseIncidentFiltersProps): UseIncidentFiltersReturn {
  const isInitialized = useRef(false);

  const [filters, setFilters] = useState<IncidentFilter[]>(() => {
    // If we have URL params, parse filters from there
    if (searchParams) {
      const urlFilters = parseFiltersFromUrl(searchParams);
      if (urlFilters.length > 0) {
        return urlFilters;
      }
    }
    // Otherwise use initial team filter
    if (initialTeamFilter) {
      return [{ column: 'Team', values: [initialTeamFilter] }];
    }
    return [];
  });

  // Sync filters to URL when they change
  useEffect(() => {
    if (!setSearchParams || !searchParams) return;
    // Skip initial render to avoid overwriting URL on mount
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    const serialized = serializeFiltersToUrl(filters);
    if (serialized) {
      newParams.set('filters', serialized);
    } else {
      newParams.delete('filters');
    }
    setSearchParams(newParams, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  // Static available values from all incidents (used for columns not yet filtered)
  const availableValues = useMemo(() => {
    const result: Record<FilterableColumn, string[]> = {} as Record<FilterableColumn, string[]>;
    FILTERABLE_COLUMNS.forEach((col) => {
      result[col] = getUniqueValues(incidents, col);
    });
    return result;
  }, [incidents]);

  // Dynamic available values that respect other active filters
  const getAvailableValuesForColumn = useCallback(
    (column: FilterableColumn): string[] => {
      const relevantIncidents = filterIncidentsExcludingColumn(incidents, filters, column);
      return getUniqueValues(relevantIncidents, column);
    },
    [incidents, filters]
  );

  const filteredIncidents = useMemo(() => {
    if (filters.length === 0) return incidents;

    return incidents.filter((incident) => filters.every((filter) => matchesFilter(incident, filter)));
  }, [incidents, filters]);

  const addFilter = useCallback((column: FilterableColumn, values: string[], isNot?: boolean) => {
    setFilters((prev) => {
      const existing = prev.find((f) => f.column === column);
      if (existing) {
        return prev.map((f) => (f.column === column ? { ...f, values, isNot } : f));
      }
      return [...prev, { column, values, isNot }];
    });
  }, []);

  const removeFilter = useCallback((column: FilterableColumn) => {
    setFilters((prev) => prev.filter((f) => f.column !== column));
  }, []);

  const updateFilter = useCallback(
    (column: FilterableColumn, values: string[], isNot?: boolean) => {
      if (values.length === 0) {
        removeFilter(column);
      } else {
        addFilter(column, values, isNot);
      }
    },
    [addFilter, removeFilter]
  );

  const clearAllFilters = useCallback(() => {
    setFilters([]);
  }, []);

  return {
    filters,
    filteredIncidents,
    availableValues,
    getAvailableValuesForColumn,
    addFilter,
    removeFilter,
    updateFilter,
    clearAllFilters,
  };
}
